/**
 * SiphoxOrderModal.tsx
 *
 * Thin LabOrderModalCore adapter for SiPhox at-home blood-test kits.
 * Staff picks the kit type and confirms the shipping address (pre-populated
 * from the patient profile). Tests section is hidden — kit types replace it
 * via the topSlot. Everything else (demographics, address, notes) reuses
 * the shared Core form.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import {
  createIntegrationOrder,
  getIntegrationCoverage,
  getSiphoxKitTypes,
  type EnabledIntegration,
  type CreateIntegrationOrderResult,
  type SiphoxKitType,
} from '@/services/integrations.service';
import { updatePatient } from '@/services/patients.service';
import { LabOrderModalCore, type LabOrderConfig, type LabOrderSubmitResult } from './LabOrderModalCore';

interface SiphoxOrderModalProps {
  patientId: number;
  integration: EnabledIntegration;
  isOpen: boolean;
  onClose: () => void;
  /** Second arg is the integrationProductId of the selected kit (for payment prefill). */
  onOrdered: (result: CreateIntegrationOrderResult, integrationProductId: number | null) => void;
}

export function SiphoxOrderModal({ patientId, integration, isOpen, onClose, onOrdered }: SiphoxOrderModalProps) {
  const [kitType, setKitType] = useState('');

  const { data: kitData, isLoading: kitTypesLoading } = useQuery({
    queryKey: ['siphox-kit-types'],
    queryFn: getSiphoxKitTypes,
    enabled: isOpen,
    // Don't serve stale empty data — credits change when the practice buys more.
    staleTime: 30_000,
  });
  const kitTypes = kitData?.kitTypes ?? [];

  useEffect(() => {
    if (isOpen && kitType === '' && kitTypes.length > 0) setKitType(kitTypes[0].type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitData, isOpen]);
  useEffect(() => { if (!isOpen) setKitType(''); }, [isOpen]);

  const { data: coverage } = useQuery({
    queryKey: ['integration-coverage', integration.key, patientId],
    queryFn: () => getIntegrationCoverage(patientId, integration.key),
    enabled: isOpen,
    staleTime: 30_000,
  });

  const selectedKit: SiphoxKitType | undefined = kitTypes.find((k) => k.type === kitType);

  // Only resolve a product when a kit type is actually selected — never fall
  // back to products[0] blindly, since that would show "Core Health" before
  // the kit types have even loaded.
  const product = selectedKit != null
    ? (selectedKit.integrationProductId != null
        ? integration.products.find((p) => p.integrationProductId === selectedKit.integrationProductId)
        : integration.products[0])
    : null;

  const price = product
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency || 'USD' }).format(product.price)
    : '';

  // Stable refs so the config.submit closure never stales.
  const kitTypeRef = useRef(kitType);
  kitTypeRef.current = kitType;
  const selectedKitRef = useRef(selectedKit);
  selectedKitRef.current = selectedKit;
  const onOrderedRef = useRef(onOrdered);
  onOrderedRef.current = onOrdered;

  // Config is stable (created once). submit reads latest values via refs.
  const config = useMemo<LabOrderConfig>(() => ({
    vendor: 'siphox',
    title: 'Order SiPhox Kit',
    requireAddress: true,
    // Staff order for a patient on file: name, DOB and gender come from the
    // chart (a blank DOB or gender can still be filled), and so do phone and
    // e-mail, which SiPhox uses to reach the patient. SiphoxIntegration reads
    // no gender, so an "Other" chart is not blocked: staff pick one for the
    // order only and the chart keeps its answer.
    lockPatientIdentity: true,
    orderGenderForOther: true,
    lockPatientContact: true,
    features: { presets: false, facilityProvider: false, billing: false, diagnoses: false, tests: false, notes: false, collectionDate: false, vitals: false },
    submit: async (payload): Promise<LabOrderSubmitResult> => {
      const kt = kitTypeRef.current;
      const sk = selectedKitRef.current;
      if (!kt) throw new Error('Please select a kit type.');
      const pid = sk?.integrationProductId ?? null;

      // A lab order may FILL a missing demographic, never change one — the
      // patient profile is the only place demographics are edited. The allowed
      // set and the was-it-blank test live in one place,
      // LabOrderModalCore.buildDemographicsFill(); name, e-mail and phone are
      // deliberately excluded.
      if (payload.demographicsFill) {
        await updatePatient(payload.patientId, payload.demographicsFill);
      }

      const result = await createIntegrationOrder(payload.patientId, {
        key: 'siphox',
        items: pid != null ? [{ integrationProductId: pid, quantity: 1 }] : undefined,
        metadata: {
          kitType: kt,
          shippingAddress: {
            recipientName: payload.recipientName || undefined,
            line1: payload.street,
            line2: payload.street2 || undefined,
            city: payload.city,
            state: payload.state,
            zip: payload.zip,
            country: payload.country || undefined,
            phone: payload.phone || undefined,
            patientShippingAddressId: payload.shippingAddressId ?? undefined,
          },
        },
      });
      onOrderedRef.current(result, pid);
      return { ok: true, lines: [`SiPhox kit ordered — Order #${result.integrationOrderId}`] };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // Price card + coverage — shown above Required Details (same spot as the
  // facility/provider bar in Quest).
  const topSlot = (coverage?.covered && coverage.membership) || product ? (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      {product && (
        <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
          <span className="text-sm font-medium text-slate-800">{product.name}</span>
          <span className="text-sm font-semibold text-slate-900">{price}</span>
        </div>
      )}
      {coverage?.covered && coverage.membership && (
        <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
          <p className="text-xs text-emerald-800">
            Patient has <span className="font-semibold">{coverage.membership.title}</span> — this kit will be
            covered by the account.
          </p>
        </div>
      )}
    </div>
  ) : null;

  // Kit type picker — sits in the same position as the Tests section in
  // Vibrant/Quest (after Required Details). Mirrors the CollapsibleSection
  // style used by the core so it feels like part of the same form.
  const itemsSlot = (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span className="text-sm font-semibold text-slate-700">Kit Type</span>
      </div>
      <div className="px-4 py-3">
        {kitTypesLoading ? (
          <p className="text-xs text-slate-400 italic">Loading available kit types…</p>
        ) : kitTypes.length === 1 ? (
          // One panel for every practice (SiPhox EXTEND) — nothing to choose.
          <p className="text-sm text-slate-800" data-testid="siphox-single-kit">
            {kitTypes[0].name || kitTypes[0].type}
          </p>
        ) : kitTypes.length > 0 ? (
          <select
            value={kitType}
            onChange={(e) => setKitType(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm w-full focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {kitTypes.map((k) => (
              <option key={k.type} value={k.type}>
                {/* No credit count: the person ordering is staff, the
                    practice's remaining kits are not theirs to track. */}
                {k.name || k.type.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-xs text-amber-700">
            {kitData?.configured === false
              ? 'SiPhox is not fully configured for this practice yet.'
              : 'No kit types available — the SiPhox account may have no remaining credits.'}
          </p>
        )}
      </div>
    </div>
  );

  // Read-only kit summary shown on the confirmation screen.
  const confirmSlot = selectedKit ? (
    <div className="space-y-1">
      <p className="font-semibold text-blue-900">Kit Type:</p>
      <p className="text-blue-800">
        {selectedKit.name || selectedKit.type}
        {product && <span className="ml-2 font-semibold">{price}</span>}
      </p>
    </div>
  ) : null;

  return (
    <LabOrderModalCore
      config={config}
      patientId={patientId}
      open={isOpen}
      onClose={onClose}
      onOrdered={() => { /* payment modal triggered inside submit via onOrderedRef */ }}
      topSlot={topSlot}
      itemsSlot={itemsSlot}
      confirmSlot={confirmSlot}
    />
  );
}
